import { formatMoney } from '@awj/i18n';
import { Button } from '@awj/ui';

import { useI18n } from '~/i18n/index.tsx';
import { buildMatrix, variantLabel, type ProductForm, type VariantDraft } from '~/lib/product-form.ts';

/**
 * Every combination the product is sold in, with its own SKU, stock and price adjustment.
 *
 * Generated on demand rather than on every option keystroke. Rebuilding automatically would mean
 * a row of typed stock counts vanishing halfway through renaming a value — so the button says
 * what it will do, and rows already filled in survive it.
 *
 * Price is shown as a *delta* because that is what the API stores: a 1 kg bag is "+108.80", not
 * "176.80", so changing the base price moves every variant with it rather than leaving six stale
 * absolute prices behind.
 */
export function VariantMatrix({
  form,
  errors = {},
  onChange,
}: {
  form: ProductForm;
  /** Keyed by variant key, so a message stays with its row. */
  errors?: Record<string, string>;
  onChange: (next: ProductForm) => void;
}) {
  const { t, locale } = useI18n();

  const base = Number(form.price) || 0;
  const expected = form.options.reduce((total, option) => total * option.values.length, 1);
  const complete = form.options.length > 0 && form.options.every((option) => option.values.length > 0);
  const stale = complete && form.variants.length !== expected;

  function patch(index: number, changes: Partial<VariantDraft>) {
    onChange({
      ...form,
      variants: form.variants.map((variant, i) => (i === index ? { ...variant, ...changes } : variant)),
    });
  }

  if (form.options.length === 0) {
    return null;
  }

  return (
    <section className="card" aria-labelledby="variants-title">
      <div className="card__head">
        <h2 className="section-label" id="variants-title">
          {t('catalog.variants')}
        </h2>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={!complete}
          onClick={() => onChange({ ...form, variants: buildMatrix(form) })}
        >
          {t('catalog.generateVariants')}
        </Button>
      </div>

      {!complete ? (
        <p className="field__hint">{t('catalog.optionsIncomplete')}</p>
      ) : stale ? (
        /*
         * The options have changed since the rows were built. Said plainly rather than fixed
         * silently: regenerating is the editor's decision, because it is the one that can lose
         * work.
         */
        <p className="notice notice--warning" role="status">
          {t('catalog.variantsOutOfDate', { expected: String(expected), actual: String(form.variants.length) })}
        </p>
      ) : null}

      {form.variants.length === 0 ? null : (
        <div className="data-table__scroll" tabIndex={0} role="region" aria-label={t('a11y.scrollableTable')}>
          <table className="data-table__table data-table--dense">
            <thead>
              <tr>
                <th className="data-table__th" scope="col">{t('catalog.variant')}</th>
                <th className="data-table__th" scope="col">{t('catalog.sku')}</th>
                <th scope="col" className="data-table__th data-table__cell--end">
                  {t('catalog.priceDelta')}
                </th>
                <th scope="col" className="data-table__th data-table__cell--end">
                  {t('catalog.finalPrice')}
                </th>
                <th scope="col" className="data-table__th data-table__cell--end">
                  {t('catalog.stock')}
                </th>
                <th className="data-table__th" scope="col">{t('catalog.active')}</th>
              </tr>
            </thead>
            <tbody>
              {form.variants.map((variant, index) => (
                <tr key={variant.key}>
                  <td className="data-table__td">
                    <bdi>{variantLabel(form, variant, locale === 'ar')}</bdi>
                  </td>
                  <td className="data-table__td">
                    <input
                      className="awj-input mono variant-input"
                      value={variant.sku}
                      aria-label={`${t('catalog.sku')} — ${variantLabel(form, variant, false)}`}
                      aria-invalid={errors[variant.key] ? true : undefined}
                      onChange={(event) => patch(index, { sku: event.target.value })}
                    />
                    {errors[variant.key] ? (
                      <span className="field__error">{errors[variant.key]}</span>
                    ) : null}
                  </td>
                  <td className="data-table__td data-table__cell--end">
                    <input
                      className="awj-input numeric variant-input variant-input--narrow"
                      type="number"
                      step="0.01"
                      value={variant.priceDelta}
                      aria-label={`${t('catalog.priceDelta')} — ${variantLabel(form, variant, false)}`}
                      onChange={(event) => patch(index, { priceDelta: event.target.value })}
                    />
                  </td>
                  <td className="data-table__td data-table__cell--end">
                    {/* What a customer will actually be charged, so the delta is checkable. */}
                    <bdi className="variant-final">
                      {formatMoney(locale, base + (Number(variant.priceDelta) || 0))}
                    </bdi>
                  </td>
                  <td className="data-table__td data-table__cell--end">
                    <input
                      className="awj-input numeric variant-input variant-input--narrow"
                      type="number"
                      min="0"
                      step="1"
                      value={variant.stockQuantity}
                      aria-label={`${t('catalog.stock')} — ${variantLabel(form, variant, false)}`}
                      onChange={(event) => patch(index, { stockQuantity: event.target.value })}
                    />
                  </td>
                  <td className="data-table__td">
                    <label className="checkbox checkbox--bare">
                      <input
                        type="checkbox"
                        checked={variant.isActive}
                        aria-label={`${t('catalog.active')} — ${variantLabel(form, variant, false)}`}
                        onChange={(event) => patch(index, { isActive: event.target.checked })}
                      />
                    </label>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
